// SPDX-License-Identifier: AGPL-3.0-or-later
// KONVO (notifications rework 2026-10, design/notifications-rework b-desk): the master switch for
// notifications on this computer as a strip at the top of the desktop page, with whether the
// browser or OS allows them and "Send a test" (one push to every device with push on).

import {ENABLE_NOTIFICATIONS_DESCRIPTOR} from '@app/features/i18n/utils/CommonMessageDescriptors';
import {Switch} from '@app/features/ui/components/form/FormSwitch';
import {BellRingingIcon, BellSlashIcon, PaperPlaneRightIcon} from '@app/features/ui/icons/Icons';
import NotificationState from '@app/features/ui/state/Notification';
import {isDesktop} from '@app/features/ui/utils/NativeUtils';
import styles from '@app/features/user/components/modals/tabs/notifications_tab/DesktopNotificationStatus.module.css';
import {useNotificationSettings} from '@app/features/user/components/modals/tabs/notifications_tab/useNotificationSettings';
import {msg} from '@lingui/core/macro';
import {useLingui} from '@lingui/react/macro';
import {clsx} from 'clsx';
import {observer} from 'mobx-react-lite';
import {useState} from 'react';

const ON_DESCRIPTOR = msg({
	message: 'Notifications on',
	comment: 'Desktop Settings > Notifications: the master strip title while notifications are enabled.',
});
const OFF_DESCRIPTOR = msg({
	message: 'Notifications off',
	comment: 'Desktop Settings > Notifications: the master strip title while notifications are disabled.',
});
const ALLOWED_APP_DESCRIPTOR = msg({
	message: 'Allowed for the Konvo app on this computer',
	comment: 'Desktop Settings > Notifications: the operating system allows notifications from the desktop app.',
});
const ALLOWED_BROWSER_DESCRIPTOR = msg({
	message: 'Allowed in this browser',
	comment: 'Desktop Settings > Notifications: the browser allows notifications from Konvo.',
});
const BLOCKED_DESCRIPTOR = msg({
	message: 'Blocked in your browser or system settings',
	comment: 'Desktop Settings > Notifications: the browser or operating system blocks notifications from Konvo.',
});
const NOT_ALLOWED_YET_DESCRIPTOR = msg({
	message: 'Not allowed yet',
	comment: 'Desktop Settings > Notifications: the browser has not been asked for permission yet.',
});
const UNSUPPORTED_DESCRIPTOR = msg({
	message: 'Not available in this browser',
	comment: 'Desktop Settings > Notifications: the browser has no notification support.',
});
const SEND_TEST_DESCRIPTOR = msg({
	message: 'Send a test',
	comment: 'Settings > Notifications: button that sends one test push notification to every device with push on.',
});

function permissionState(): NotificationPermission | 'unsupported' {
	return typeof Notification === 'undefined' ? 'unsupported' : Notification.permission;
}

export const DesktopNotificationStatus = observer(() => {
	const {i18n} = useLingui();
	const {toggleNotifications, sendTestNotification} = useNotificationSettings();
	const [sending, setSending] = useState(false);
	const enabled = NotificationState.browserNotificationsEnabled;
	const permission = permissionState();
	const status =
		permission === 'granted'
			? i18n._(isDesktop() ? ALLOWED_APP_DESCRIPTOR : ALLOWED_BROWSER_DESCRIPTOR)
			: permission === 'denied'
				? i18n._(BLOCKED_DESCRIPTOR)
				: permission === 'default'
					? i18n._(NOT_ALLOWED_YET_DESCRIPTOR)
					: i18n._(UNSUPPORTED_DESCRIPTOR);
	return (
		<div className={clsx(styles.strip, !enabled && styles.off)} data-flx="user.notifications-tab.status">
			<span className={styles.tile} aria-hidden="true">
				{enabled ? <BellRingingIcon size={22} /> : <BellSlashIcon size={22} />}
			</span>
			<span className={styles.text}>
				<span className={styles.title}>{i18n._(enabled ? ON_DESCRIPTOR : OFF_DESCRIPTOR)}</span>
				<span className={styles.status}>
					<i
						className={clsx(
							styles.dot,
							permission === 'granted' && styles.dotOk,
							permission === 'denied' && styles.dotBad,
						)}
					/>
					{status}
				</span>
			</span>
			<button
				type="button"
				className={styles.test}
				disabled={sending}
				onClick={() => {
					setSending(true);
					void sendTestNotification().finally(() => setSending(false));
				}}
				data-flx="user.notifications-tab.status.send-test"
			>
				<PaperPlaneRightIcon size={16} />
				{i18n._(SEND_TEST_DESCRIPTOR)}
			</button>
			<Switch
				compact
				value={enabled}
				onChange={toggleNotifications}
				ariaLabel={i18n._(ENABLE_NOTIFICATIONS_DESCRIPTOR)}
				data-flx="user.notifications-tab.status.switch"
			/>
		</div>
	);
});
